import type { Messages } from '../messages.js'

/**
 * Serbian, in Cyrillic. `потез` for flips, the word a Serbian board game uses for a move.
 *
 * Serbian is written in two alphabets and this is the official one. The Latin spelling is the
 * same words letter for letter, so it would be a second language here rather than a second
 * spelling of this one.
 */
export const sr: Messages = {
  tag: 'sr',

  readingDictionary: 'Читам речник…',
  noWordList: 'Нема списка речи за „{language}“. Направите га:  pnpm dictionary build',
  emptyWordList: 'Списак речи за „{language}“ је празан.',

  flips: 'потези',
  score: 'резултат',
  words: 'речи',
  round: 'рунда',
  ticksLeftLabel: 'Преостало време у овој рунди',
  typeAWord: 'укуцајте реч',
  tapPrompt: 'додирните слова да их изаберете или вратите, па {action}',

  boardOfTiles: 'Табла од {n} плочица',
  faceDown: 'окренуто наопако',
  wildCard: 'џокер',
  wildKey: 'било које слово',
  letterReplaced: '{from} је постало {to}',
  letterSwap: 'ЗАМЕНА СЛОВА!',
  spentTile: 'искоришћена плочица',
  hiddenWhilePaused: 'скривено током паузе',
  letterInWord: '{letter}, {position}. слово речи',

  completeWord: 'Заврши реч',

  completeShort: 'Заврши',
  reset: 'Обриши',
  pause: 'Пауза',
  resume: 'Настави',
  newGame: 'Нова игра',
  paused: 'Паузирано',
  outOfFlips: 'Нестало потеза',
  finalResult: '{score} за {words} кроз {rounds}',
  playAgain: 'Играј поново',
  share: 'Подели',
  shareCopied: 'Копирано.',
  shareSelect: 'Копирајте ово:',

  lettersSelect: 'слова бирају',
  keysWild: 'узима се када укуцате слово које ниједна плочица не показује',
  clearsEvery: 'брише сва изабрана {letter}',
  undoLastLetter: 'поништи последње слово',
  noWordsYet: 'Још ниједна реч.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'већ пронађено',
  reasonTooShort: 'прекратко',
  reasonNotAWord: 'то није реч',
  reasonAllFound: 'већ их имате све',
  noSuchLetterUp: 'нема откривеног {letter}',
  nothingUp: 'ништа није откривено',
  letterHidden: 'једно слово се сакрило',
  shuffled: 'промешано',
  shuffledAndBilled: 'промешано, наплаћено {flips} неискоришћених',
  tooFewLetters: 'Остало је премало слова',

  gameLanguage: 'језик',
  interfaceLanguage: 'сучеље',
  dictionarySize: '{common} свакодневних од {full} речи',
  filterLanguages: 'Тражи језик',
  noMatches: 'Нема поклапања',

  nerdMode: 'режим за зналце',
  rules: 'Правила',
  difficulty: 'тежина',
  difficultyNames: { easy: 'лака', medium: 'средња', hard: 'тешка', insane: 'луда' },
  pausedNoBoard: 'Ова игра неће доћи на ранг-листу.',
  about: 'О игри',
  themeLabel: 'тема',
  themeNames: { traditional: 'традиционална', light: 'светла', contrast: 'висок контраст' },
  tiles: 'плочице (N)',
  secondsPerTick: 'секунде / такт',
  holdTicks: 'тактови задржавања',
  minWord: 'најкраћа реч',
  startingFlips: 'почетни потези',
  wildChance: 'шанса за џокера',
  replaceChance: 'шанса за замену слова',
  hideChance: 'шанса за скривање слова',
  wordCompleteMode: 'када је реч готова',
  wordCompleteNames: { shuffle: 'промешај', spend: 'потроши', keep: 'задржи' },
  flipEconomy: 'повраћај потеза',
  flipEconomyNames: {
    none: 'нема',
    perLetter: 'по слову',
    fibonacci: 'фибоначи',
    overMinimum: 'преко минимума',
  },
  repeatedLetterKey: 'тастер поновљеног слова',
  keySchemeNames: { cycle: 'круг', advance: 'напред' },
  keySchemeHelp: {
    cycle:
      'А узима следеће неискоришћено А, а када су сва у речи, брише их. ' +
      'Shift+А их такође брише.',
    advance: 'А узима следеће неискоришћено А. Shift+А брише из речи сва А.',
  },

  whatThatMeans: 'Шта то значи',
  factRound: 'рунда',
  factWholeBoardUp: 'цела табла откривена',
  factRoundCosts: 'рунда кошта',
  factFlipsBuy: 'почетни потези купују',
  factThisBoard: 'ова табла',
  factBoardHadToAdmit: 'табла је морала да прими',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, најдужа {longest}',
  wordsIncludingOneOf: '{words}, од тога једна од {ceiling}',
  scorelessRounds: '{rounds} без поена',

  whatAWordPays: 'Шта доноси реч',
  columnLetters: 'слова',
  columnCost: 'цена',
  columnPoints: 'поени',
  columnFlips: 'потези',
  columnNet: 'разлика',

  canonicalRules: 'Уобичајена правила за {difficulty}.',
  customRules: 'Измењено у односу на подешено. Резултати по сопственим правилима се не рангирају.',
  applyAndStart: 'Примени и почни нову игру',
  changesNextGame: 'Измене важе од следеће игре.',
  presets: 'Подешавања:',

  start: 'Почни',
  restart: 'Испочетка',
  quit: 'Заврши',
  quitTitle: 'Завршити ову игру?',
  restartTitle: 'Почети ову игру испочетка?',
  restartConfirm: 'Испочетка',
  quitConfirm: 'Заврши',
  keepPlaying: 'Настави да играш',
  personalBest: 'Ваше најбоље игре',
  newPersonalBest: 'Нов лични рекорд.',
  columnRank: '#',
  notRanked: 'Сопствена правила, па се ова игра не рангира.',
  notRankedPaused: 'Сат се зауставио, па се ова игра не рангира.',
  rankOfTotal: '{rank} од {total}',

  howToPlay: 'Како се игра',

  backToGame: 'Назад на игру',
  welcomeTitle: 'Добро дошли у Blinkered',
  tutorialSkip: 'Прескочи',
  tutorialNext: 'Даље',
  tutorialBack: 'Назад',
  tutorialStart: 'Почни да играш',
  tutorialHideAgain: 'Не приказуј ово поново',
  tutorialProgress: '{n} од {total}',
  tutorialSkipTitle: 'Прескочити упознавање?',
  tutGoalTitle: 'Blinkered је трка',
  tutGoalBody:
    'Игру почињете са одређеним бројем потеза, а свака откривена плочица кошта један потез. Када потези нестану, игра је готова. Речи доносе потезе, и што је реч дужа, то више потеза добијате.',
  tutSpentBody:
    'Слова која употребите у речи окрећу се наопако до следеће рунде. Само замена слова мења која су слова на табли.',
  tutChangeTitle: 'Слова могу да се мењају',
  tutSameLetters:
    'Уочили сте реч коју нисте стигли да одиграте? Иста слова се враћају у следећој рунди, измешана на нова места.',
  tutPickLetters: 'Додирујте слова која желите, редом, да саставите реч.',
  tutMoreTurn: 'Плочице се окрећу и док размишљате, па боље слово можда тек долази.',
  tutTapBack:
    'Узели сте слово које нисте желели? Додирните га поново и вратиће се. Било које, не само последње.',
  tutComplete: 'Када је реч спремна, притисните Заврши.',
  htBoardTitle: 'Табла',
  htBoardBody:
    'Плочице се откривају једна по једна, случајним редом. Од откривених се састављају речи.',
  htWordsTitle: 'Речи',
  htWordsBody: 'Саставите реч од откривених плочица куцањем или кликом на слова редом.',
  htFlipsTitle: 'Потези',
  htFlipsBody:
    'Свака откривена плочица кошта потез. Завршена реч враћа потезе, а дуже речи враћају више. Када потези нестану, игра је готова.',
  htRoundTitle: 'Рунда',
  htRoundBody:
    'Када се открије последња плочица рунде, цела табла на тренутак стоји. Затим се плочице окрећу и мешају, и почиње нова рунда.',
  htLanguagesTitle: 'Језици',
  htLanguagesBody:
    'Има их {n}. Свака табла се може решити речима које људи стварно користе. Ретка реч такође доноси поене, ако је речник зна.',
  htKeysTitle: 'Тастатура',
  htWildTitle: 'Џокери',
  htWildBody:
    'Понекад се уместо слова појави џокер. Џокер важи као било које слово које чини исправну реч. Већ завршена реч се не рачуна.',
  htHideTitle: 'Слова која се скривају',
  htHideBody:
    'На тежим нивоима откривено слово може само да се преврне и да се врати касније у кругу. Тајмер за то добија додатну цртицу, у црвеном. Већ изабрана слова не преврћу се.',
  tutHideBody:
    'На тежим нивоима откривено слово може само да се преврне и да се врати касније у кругу. Већ изабрана слова не преврћу се.',
  htSwapTitle: 'Слова која се мењају',
  htSwapBody:
    'Понекад се између рунди једно слово замени другим. Видећете које је отишло и које је дошло.',
  htLevelsTitle: 'Нивои',
  htLevelEasy:
    'Истих дванаест слова целу игру, па се могу научити и носити списак речи у глави. Плочице се откривају споро, а цела табла остаје видљива довољно дуго да завршите избор.',
  htLevelMedium:
    'С времена на време слово се промени, па је теже памтити речи остављене за касније. Мање времена за гледање и мање за размишљање.',
  htLevelHard:
    'Речи од три слова престају да се рачунају, а слово се мења отприлике сваку другу рунду. Табла једва да се покаже, а већ се меша.',
  htLevelInsane: 'Све одједном и пуном брзином. Табла се меша готово одмах после последњег потеза.',
  htTouchTitle: 'Екран на додир',
  htTouchBody:
    'Додирните откривену плочицу да узмете њено слово. Додирните узето слово да га вратите. Заврши и Обриши су испод табле.',

  accountTitle: 'Твој налог',
  menuProfile: 'Мој профил',
  menuGames: 'Моје игре',
  menuPublicPage: 'Моја јавна страница',
  menuSignedInAs: 'Пријављен као',
  signIn: 'Пријава',
  signOut: 'Одјава',
  signInTitle: 'Пријави се или отвори налог',
  signInLead: 'Налог чува твоје игре, на сваком уређају.',
  signInKeepGame: 'Пријави се да сачуваш ову игру, и сваку следећу.',
  continueWithApple: 'Настави са Apple',
  continueWithGoogle: 'Настави са Google',
  signInOr: 'или',
  emailLabel: 'Е-пошта',
  codeLabel: 'Код',
  emailMeACode: 'Пошаљите ми код',
  working: 'Тренутак…',
  sendNewCode: 'Пошаљи нови код',
  codeSent: 'Погледај пошту. Код важи 10 минута.',
  notNow: 'Не сада',
  badEmail: 'Ово не личи на адресу е-поште.',
  badCode: 'Тај код није радио. Кодови истичу после 10 минута и важе једном.',
  serverBusy: 'Сервер није доступан. Покушај за тренутак.',
  serverDown: 'Сервер није доступан.',
  offline: 'Офлајн',
  leaderboardTitle: 'Ранг-листа',
  leaderboardEmpty: 'На овој листи још нема игара. Буди први.',
  leaderboardWouldBe: 'Где би стајала ова игра',
  leaderboardThisGame: 'Ова игра',
  leaderboardRanksHere: 'Где стоји ова игра',
  leaderboardOutside: 'Ван прве петорке.',
  tutAccountTitle: 'Сачувај своје резултате',
  tutAccountBody: 'Рачун чува твоје игре, поставља резултате на ранг-листу и одржава твој профил.',
  ssoCancelled: 'Пријава је отказана.',
  ssoExpired: 'Предуго је трајало. Пријави се поново.',
  ssoNoUsername: 'Налог није могао да се доврши. Покушај за тренутак.',
  ssoFailed: 'Није успело. Покушај поново или користи е-пошту.',
  tabProfile: 'Профил',
  countryAny: 'Радије не бих рекао',
  findCountry: 'Тражи државе',
  usernameLabel: 'Корисничко име',
  bioLabel: 'О мени',
  countryLabel: 'Држава',
  bioHint: 'Без линкова. {left}/{max}',
  save: 'Сачувај',
  saving: 'Чувам…',
  saved: 'Сачувано.',
  nameTaken: 'То име неко већ има.',
  nameTooShort: 'Мало дуже, молим.',
  nameTooLong: 'Предуго је.',
  nameBadCharacters: 'Само слова, бројеви, цртице и доње црте.',
  nameBadEdges: 'Мора почети и завршити словом или бројем.',
  nameMixedScripts: 'Изабери једно писмо и остани у њему.',
  nameReserved: 'Ово личи на име које делимо ми. Изабери друго.',
  nameUnusable: 'То име не може да се користи.',
  bioTooLong: 'Опис има највише {max} знакова.',
  bioHasLink: 'Линкови у опису нису дозвољени.',
  bioHasControl: 'Садржи знакове које опис не може да има.',
  bioUnusable: 'Тај опис не може да се користи.',
  gamesHeading: 'Игре',
  gamesEmpty: 'Овде још нема ничега. Игре одигране док си пријављен појавиће се овде.',
  gamesEmptyShort: 'Овде још нема ничега.',
  gamesLoading: 'Читам твоје игре…',
  theirGamesLoading: 'Читам њихове игре…',
  profileLoading: 'Читам профил…',
  playerNotFound: 'Овде нема никога с тим именом.',
  columnWhen: 'Када',
  columnRounds: 'рунди',
  columnGame: 'Игра',
  gameLoading: 'Читам игру…',
  gameUnreadable: 'Ову игру није било могуће прочитати.',
  gameNotRanked: 'Одиграна са измењеним правилима, па се не рангира.',
  detailNotKept: 'Речи и табле ове игре више се не чувају.',
  openingBoard: 'Почетна табла',
  roundByRound: 'Рунду по рунду',
  roundNothing: 'ништа није нађено',
  changeReplaced: '{letter} замењено на месту {slot}',
  changeWildOn: '{letter} џокер на месту {slot}',
  changeWildOff: '{letter} џокер уклоњен с места {slot}',
  keepThisGame: 'Сачувај ову игру',
  savedToYourGames: 'Сачувано међу твоје игре.',

  reportThis: 'Пријави',
  reportHeading: 'Пријави ово',
  reportTheName: 'Име',
  reportTheBio: 'Опис',
  reportTheScore: 'Резултат',
  reportWhy: 'Шта с тим није у реду? Необавезно.',
  reportSend: 'Пошаљи пријаву',
  reportSent: 'Хвала. Неко ће то погледати.',
  reportSignIn: 'За ово мораш бити пријављен.',
  reportGone: 'Тога више нема.',
  reportCancel: 'Одустани',

  deleteAccount: 'Избриши мој рачун',
  deleteAccountWhat: 'Ово заувек уклања твој профил и сваку сачувану игру. Не може се вратити.',
  deleteAccountAsk: 'Пошаљите ми код',
  deleteConfirm: 'Избриши заувек',
  deleteGone: 'Твог рачуна више нема. Хвала што си играо.',
  deleteNoAddress:
    'На овом рачуну нема потврђене адресе, па се брисање не може проверити. Јави нам се и урадићемо то ручно.',

  rulesetFacts: 'Речи од {letters} · {seconds} по плочици',
  plurals: {
    words: { one: '{n} реч', few: '{n} речи', other: '{n} речи' },
    rounds: { one: '{n} рунда', few: '{n} рунде', other: '{n} рунди' },
    flips: { one: '{n} потез', few: '{n} потеза', other: '{n} потеза' },
    ticks: { one: '{n} такт', few: '{n} такта', other: '{n} тактова' },
    points: { one: '{n} поен', few: '{n} поена', other: '{n} поена' },
    minLetters: { one: 'најмање {n} слова', few: 'најмање {n} слова', other: 'најмање {n} слова' },
    seconds: { one: '{n} секунда', few: '{n} секунде', other: '{n} секунди' },
  },
}
